'use client';
import { useModalContext } from '#pplx/frontend/aether/modals/context/useModalContext.js';
import { useToastContext } from '#pplx/frontend/aether/toasts/context/useToastContext.js';
import { proCheckoutStartedEvent } from '#pplx/frontend/apps/ask/events/billing/analytics.js';
import { enterpriseProOnboardingStartedEvent } from '#pplx/frontend/apps/ask/events/enterprise-onboarding/analytics.js';
import { useBrandedVisitorGate } from '#pplx/frontend/apps/ask/hooks/branded-visitor-gate-hooks/useBrandedVisitorGate.js';
import { useDiscountCode } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/useDiscountCode.js';
import { getPaymentPath, useNavigateToPayment, } from '#pplx/frontend/apps/ask/hooks/subscription-hooks/useNavigateToPayment.js';
import { usePersistentPromoCode } from '#pplx/frontend/apps/ask/hooks/subscription-hooks/usePersistentPromoCode.js';
import { shouldOpenSubscriptionOnWeb } from '#pplx/frontend/apps/ask/hooks/subscription-hooks/webSubscriptionRedirect.js';
import { BILLING_PERIOD_PARAM_NAME } from '#pplx/frontend/apps/ask/onboarding/hooks/useBillingPeriod.js';
import { useEnterpriseSeatCapacityRolloutStage } from '#pplx/frontend/apps/ask/organization/hooks/useEnterpriseSeatCapacityRolloutStage.js';
import { fetchOrganizationMemberUpdateSubscriptionTierPreview, useOrganizationMemberUpdateSubscriptionTier, } from '#pplx/frontend/apps/ask/organization/hooks/useOrganizationMemberUpdateSubscriptionTier.js';
import { useUserOrgData } from '#pplx/frontend/apps/ask/organization/hooks/useUserOrgData.js';
import { getSubscriptionTierForEnterpriseTier } from '#pplx/frontend/apps/ask/organization/types/enterpriseTiers.js';
import { ENTERPRISE_ONBOARDING_RETURN_TO_PARAM } from '#pplx/frontend/apps/ask/organization/utils/enterpriseOnboarding.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { createCheckoutSession } from '#pplx/frontend/apps/ask/queries/billing-queries/stripe/createCheckoutSession.js';
import { invalidateSubscriptionQueries } from '#pplx/frontend/apps/ask/queries/upsell-queries/invalidateSubscriptionQueries.js';
import { routes } from '#pplx/frontend/apps/ask/routing/spaRoutes.js';
import { useDefaultSearchModel } from '#pplx/frontend/apps/ask/search/hooks/useDefaultSearchModel.js';
import { usePreferredSearchModels } from '#pplx/frontend/apps/ask/settings/hooks/usePreferredSearchModels.js';
import { SearchMode, SearchModel } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { useIsUserLoggedIn } from '#pplx/frontend/auth/userHooks.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { toPublicWebUrl } from '#pplx/frontend/common/location.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { getChosenLocale } from '#pplx/frontend/i18n/browser.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { redirectWindow } from '#pplx/frontend/platform-components/redirectWindow.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import { useSearchParams } from '#pplx/frontend/router/useSearchParams.js';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
const enterpriseEmailRequestGeneratedEvent = EVENT_PAYLOAD;
const MemberSetTierModalDynamic = Dynamic(async () => {
    const { MemberSetTierModal } = await import('#pplx/frontend/apps/ask/organization/components/settings/member-set-tier-modal/MemberSetTierModal.js');
    return { default: MemberSetTierModal };
});
const enterpriseExistingSeatAssignmentSelectedEvent = EVENT_PAYLOAD;
const enterpriseEmailSubjectMessages = defineMessages({
    pro: {
        defaultMessage: 'Perplexity Enterprise Pro Request',
        id: '8f77156898',
        description: 'Email subject line for an Enterprise Pro upgrade request to admins',
    },
    max: {
        defaultMessage: 'Perplexity Enterprise Max Request',
        id: '83a0b1ba60',
        description: 'Email subject line for an Enterprise Max upgrade request to admins',
    },
});
const enterpriseEmailBodyUpgradeMessages = defineMessages({
    pro: {
        defaultMessage: "Dear Admin Team,%0D%0A%0D%0AI'd like to request an upgrade to Perplexity Enterprise Pro for our team. It would give us unlimited searches with the top AI models, better research tools, and custom workspaces while ensuring data security and administrative controls.%0D%0A%0D%0ABest regards,%0D%0A[Your Name]",
        id: '23780a735c',
        description: 'Email body template for a member requesting an Enterprise Pro upgrade from admins',
    },
    max: {
        defaultMessage: 'Hi there,%0D%0A[requestor email] is requesting an upgrade to Perplexity Enterprise Max, our most powerful tier yet.%0D%0A%0D%0AIt would give them access to:%0D%0A• Unlimited access to deep research and file and app creation – get comprehensive research reports, build dashboards, analyze data, and create interactive web apps–all in one place%0D%0A• Access to advanced AI models - Get in-depth answers powered by o3-pro, GPT 5 Thinking, or Opus 4.1 Thinking%0D%0A• Greater file upload limits - from 5,000 to 10,000 in your files and 500 to 5,000 in Spaces%0D%0A• Enhanced video generation - from 3 to 15 videos a month with sound powered by Veo 3%0D%0A%0D%0A[Upgrade your team member to Enterprise Max]',
        id: '793aa9d182',
        description: 'Email body template for a member requesting an Enterprise Max upgrade from admins',
    },
});
const enterpriseEmailBodyDowngradeMessages = defineMessages({
    pro: {
        defaultMessage: "Dear Admin Team,%0D%0A%0D%0AI'd like to request a downgrade to Perplexity Enterprise Pro, as I no longer require the additional functionality offered by Enterprise Max.%0D%0A%0D%0ABest regards,%0D%0A[Your Name]",
        id: 'ca8984bf5c',
        description: 'Email body template for a member requesting a downgrade from Enterprise Max to Pro',
    },
});
function getEmailLinkBody({ toTier, fromTier, $t }) {
    if (toTier === 'pro' && fromTier === 'max') {
        return $t(enterpriseEmailBodyDowngradeMessages.pro);
    }
    return $t(enterpriseEmailBodyUpgradeMessages[toTier]);
}
function getEmailLink({ toTier, fromTier, organization, $t }) {
    const to = organization?.admins
        ?.map((admin) => admin?.email)
        .filter((email) => email !== undefined)
        .join(';');
    const subject = $t(enterpriseEmailSubjectMessages[toTier]);
    const body = getEmailLinkBody({ toTier, fromTier, $t });
    return `mailto:${to}?subject=${subject}&body=${body}`;
}
function toAbsoluteRedirectUrl(redirectPath) {
    if (!redirectPath || typeof window === 'undefined')
        return undefined;
    try {
        return toPublicWebUrl(redirectPath);
    }
    catch {
        return undefined;
    }
}
export const useHandleClickPricing = ({ reason, redirectPath, closePath, enterpriseOnboardingReturnTo, resumeCheckoutAfterLogin, }) => {
    const { $t } = useIntl();
    const router = useRouter();
    const queryClient = useQueryClient();
    const { hasAccessToProFeatures, isEnterprisePro, isEnterpriseMax } = useIsSubscribed();
    const { discountCode } = useDiscountCode();
    const [persistentPromoCode] = usePersistentPromoCode();
    const searchParams = useSearchParams();
    const enterprisePromoCode = searchParams?.get('promotion_code') || persistentPromoCode || undefined;
    const isUserLoggedIn = useIsUserLoggedIn();
    const { openVisitorLoginUpsell } = useBrandedVisitorGate({
        enabled: !isUserLoggedIn,
    });
    const { update: updateSession } = useSession();
    const { track } = useInstrumentation();
    const { organization, orgUser, isAdmin } = useUserOrgData({ reason });
    const { subscriptionTier } = useIsSubscribed();
    const { openModal } = useModalContext();
    const { stage, loading: isRolloutStageLoading } = useEnterpriseSeatCapacityRolloutStage();
    const { openToast } = useToastContext();
    const { setPreferredSearchModel } = usePreferredSearchModels();
    const { setConfiguredModel } = useThreadConfig();
    const defaultSearchModel = useDefaultSearchModel();
    const { mutateAsync: updateSubscriptionTier } = useOrganizationMemberUpdateSubscriptionTier({
        reason,
        onSuccess: async () => {
            await updateSession();
            invalidateSubscriptionQueries(queryClient);
        },
    });
    const startTierChange = useCallback(async (to) => {
        if (!orgUser?.uuid)
            return;
        const member = {
            email: orgUser.email,
            id: orgUser.uuid,
            subscription_tier: subscriptionTier ?? 'pro',
        };
        const openTierModal = () => {
            openModal(MemberSetTierModalDynamic, {
                member,
                to,
                isSelfChange: true,
                onSuccess: () => invalidateSubscriptionQueries(queryClient),
            }, { replace: false });
        };
        if (isRolloutStageLoading || stage !== 'deferred_reductions') {
            openTierModal();
            return;
        }
        try {
            const [preview, { isMemberTierChangeCoveredByExistingSeat }] = await Promise.all([
                fetchOrganizationMemberUpdateSubscriptionTierPreview({
                    reason,
                    orgUserUUID: member.id,
                    tier: to,
                }),
                import('#pplx/frontend/apps/ask/organization/hooks/paidSeatCapacityConfirmation.js'),
            ]);
            const isCovered = isMemberTierChangeCoveredByExistingSeat({
                from: member.subscription_tier,
                preview,
                to,
            });
            if (!isCovered || !preview.seat_capacity_preview || preview.would_lose_premium_secure) {
                openTierModal();
                return;
            }
            track(enterpriseExistingSeatAssignmentSelectedEvent({
                name: 'enterprise existing seat assignment selected',
                data: {
                    confirmationKind: 'covered',
                    fromTier: member.subscription_tier,
                    interaction: 'bypass',
                    orgUserUUID: member.id,
                    source: 'member_tier_change',
                    toTier: to,
                },
            }));
            await updateSubscriptionTier({
                orgUserUUID: member.id,
                tier: to,
                billingInterval: preview.subscription_interval,
                disablePremiumSecure: preview.would_lose_premium_secure ?? false,
                seatCapacityPreview: preview.seat_capacity_preview,
            }, {
                onSuccess: () => {
                    setPreferredSearchModel(SearchMode.SEARCH, defaultSearchModel);
                    setConfiguredModel(defaultSearchModel);
                },
            }).catch(() => undefined);
        }
        catch {
            openTierModal();
        }
    }, [
        isRolloutStageLoading,
        openModal,
        orgUser,
        queryClient,
        reason,
        stage,
        subscriptionTier,
        track,
        updateSubscriptionTier,
        defaultSearchModel,
        setConfiguredModel,
        setPreferredSearchModel,
    ]);
    const { navigateToPayment } = useNavigateToPayment();
    const { device: { isWindowsApp }, } = useWebPlatform();
    const handleClickPersonal = useCallback(async (args) => {
        const { billingPeriod, subscriptionTier = 'pro', origin, onCheckout } = args;
        if (!BUILD_CAPABILITIES.payments)
            return;
        const paymentDiscountCode = discountCode ?? persistentPromoCode ?? undefined;
        if (isUserLoggedIn) {
            if (onCheckout && canUseModalCheckout() && !isWindowsApp) {
                track(proCheckoutStartedEvent({
                    name: 'pro checkout started',
                    data: {
                        subscriptionTier,
                        origin,
                        discountCode: paymentDiscountCode ?? '',
                        tier: billingPeriod,
                        flowType: 'embedded_checkout',
                    },
                }));
                onCheckout({
                    segment: 'consumer',
                    subscriptionTier,
                    origin,
                    billingPeriod,
                    discountCode: paymentDiscountCode,
                    redirectPath,
                });
                return;
            }
            if (await navigateToPayment({
                origin,
                discountCode: paymentDiscountCode,
                tier: billingPeriod,
                subscriptionTier,
                redirectPath,
                closePath,
            })) {
                return;
            }
            track(proCheckoutStartedEvent({
                name: 'pro checkout started',
                data: {
                    subscriptionTier,
                    origin,
                    discountCode: discountCode ?? '',
                    tier: billingPeriod,
                    flowType: 'stripe_checkout',
                },
            }));
            const response = await createCheckoutSession({
                request: {
                    tier: billingPeriod,
                    discountCode: !hasAccessToProFeatures ? discountCode : '',
                    origin,
                    locale: getChosenLocale(),
                    subscriptionTier,
                    success_redirect_url: toAbsoluteRedirectUrl(redirectPath),
                },
                reason,
            });
            if (response?.url) {
                redirectWindow(response.url, `Handle click pricing ${billingPeriod} checkout started`);
            }
            else {
                router.refresh(`Handle click ${billingPeriod}`);
            }
        }
        else {
            const overrideRedirectUrl = resumeCheckoutAfterLogin
                ? toPublicWebUrl(getPaymentPath({
                    origin,
                    discountCode: paymentDiscountCode,
                    tier: billingPeriod,
                    subscriptionTier,
                    redirectPath,
                    closePath,
                }))
                : undefined;
            openVisitorLoginUpsell({
                title: $t({
                    defaultMessage: 'Sign in to unlock {tier}',
                    id: '2320e49e8c',
                    description: 'Title for the visitor login modal when attempting to subscribe to a tier',
                }, { tier: subscriptionTier === 'max' ? 'Max' : 'Pro' }),
                origin,
                overrideRedirectUrl,
            });
        }
    }, [
        isUserLoggedIn,
        navigateToPayment,
        discountCode,
        track,
        hasAccessToProFeatures,
        router,
        openVisitorLoginUpsell,
        $t,
        persistentPromoCode,
        reason,
        redirectPath,
        closePath,
        resumeCheckoutAfterLogin,
        isWindowsApp,
    ]);
    /**
     * Navigate an unpaid existing-org admin directly to checkout.
     *
     * Reuses the shared fetchOrCreateDraft (same as OnboardingDraftContext),
     * then patches in the subscription tier and existing org name (required by
     * the backend checkout handler), then routes to the embedded checkout page.
     *
     * Draft helpers are dynamically imported to keep them out of the bootstrap
     * chunk — they are only needed when an unpaid org admin actually clicks the
     * CTA.
     */
    const navigateUnpaidOrgAdminToCheckout = useCallback(async (tier, billingPeriod, origin, onCheckout) => {
        const { fetchOrCreateDraft, updateDraftFields } = await import('#pplx/frontend/apps/ask/queries/enterprise-queries/fetchOrCreateDraft.js');
        const subscriptionTier = getSubscriptionTierForEnterpriseTier(tier);
        const draft = await fetchOrCreateDraft();
        // The checkout handler requires org_name and subscription_tier on the
        // draft. For existing orgs we use the org's current display_name.
        await updateDraftFields(draft.uuid, {
            subscription_tier: subscriptionTier,
            org_name: organization?.display_name ?? '',
            last_step: 'org_info',
        });
        if (onCheckout && canUseModalCheckout() && !isWindowsApp) {
            onCheckout({
                segment: 'enterprise',
                subscriptionTier,
                billingPeriod,
                origin,
                orgDraftUuid: draft.uuid,
                discountCode: enterprisePromoCode,
            });
            return;
        }
        const plan = billingPeriod === 'yearly' ? 'yearly' : 'monthly';
        router.push(`/checkout/enterprise?subscriptionTier=${subscriptionTier}&orgDraftUuid=${draft.uuid}&plan=${plan}`);
    }, [organization?.display_name, router, enterprisePromoCode, isWindowsApp]);
    const handleClickEnterprise = useCallback(async (args) => {
        const { tier = 'enterprise_pro', origin, billingPeriod, onCheckout } = args;
        if (!BUILD_CAPABILITIES.payments)
            return;
        if (orgUser && organization) {
            if (isAdmin) {
                // Admin with active enterprise subscription - directly update tier
                if (isEnterprisePro || isEnterpriseMax) {
                    const userId = orgUser.uuid;
                    const toTier = getSubscriptionTierForEnterpriseTier(tier);
                    if (userId) {
                        if (isEnterprisePro && toTier === 'max') {
                            await startTierChange(toTier);
                            return;
                        }
                        await updateSubscriptionTier({
                            orgUserUUID: userId,
                            tier: toTier,
                        }).catch(() => undefined);
                    }
                }
                else {
                    // Admin with existing org but no active subscription (billing_status=UNPAID).
                    // Route directly to checkout instead of /onboarding/org/create,
                    // which redirects users who already have an org to org details.
                    await navigateUnpaidOrgAdminToCheckout(tier, billingPeriod, origin, onCheckout).catch((err) => {
                        logger.error('Failed to navigate unpaid org admin to checkout', err);
                        openToast({
                            message: $t({
                                defaultMessage: 'Something went wrong. Please try again.',
                                id: '71489d437c',
                                description: 'Error toast when enterprise checkout navigation fails for unpaid org admin',
                            }),
                            variant: 'error',
                            timeoutSeconds: 5,
                        });
                        if (onCheckout)
                            throw err;
                        router.push(routes['/'].href());
                    });
                }
                return;
            }
            // Non-admin - send email to admins (if allowed)
            if (organization?.settings?.allow_member_upgrade_requests === false) {
                return;
            }
            const toTier = getSubscriptionTierForEnterpriseTier(tier);
            const emailHref = getEmailLink({
                toTier,
                fromTier: subscriptionTier,
                organization,
                $t,
            });
            track(enterpriseEmailRequestGeneratedEvent({
                name: 'enterprise email request generated',
                data: {
                    toTier,
                    fromTier: subscriptionTier,
                    origin,
                },
            }));
            redirectWindow(emailHref, 'Handle click enterprise');
            return;
        }
        track(enterpriseProOnboardingStartedEvent({
            name: 'enterprise pro onboarding started',
            data: {
                origin,
            },
        }));
        if (onCheckout && isUserLoggedIn && canUseModalCheckout() && !isWindowsApp) {
            onCheckout({
                segment: 'enterprise',
                subscriptionTier: getSubscriptionTierForEnterpriseTier(tier),
                billingPeriod,
                origin,
                discountCode: enterprisePromoCode,
            });
            return;
        }
        const params = new URLSearchParams({
            tier,
            [BILLING_PERIOD_PARAM_NAME]: billingPeriod,
        });
        if (enterpriseOnboardingReturnTo) {
            params.set(ENTERPRISE_ONBOARDING_RETURN_TO_PARAM, enterpriseOnboardingReturnTo);
        }
        const orgCreatePath = `/onboarding/org/create?${params.toString()}`;
        if (isWindowsApp) {
            redirectWindow(`https://www.perplexity.ai${orgCreatePath}`, 'Handle click enterprise WindowsApp');
        }
        else {
            router.push(orgCreatePath);
        }
    }, [
        orgUser,
        isAdmin,
        isWindowsApp,
        router,
        updateSubscriptionTier,
        organization,
        $t,
        track,
        isEnterprisePro,
        isEnterpriseMax,
        subscriptionTier,
        startTierChange,
        navigateUnpaidOrgAdminToCheckout,
        openToast,
        enterpriseOnboardingReturnTo,
        isUserLoggedIn,
        enterprisePromoCode,
    ]);
    return useMemo(() => ({
        handleClickPersonal,
        handleClickEnterprise,
    }), [handleClickPersonal, handleClickEnterprise]);
};
function canUseModalCheckout() {
    return (!shouldOpenSubscriptionOnWeb() &&
        !import.meta.env.VITE_IS_SIDECAR_BUILD &&
        !import.meta.env.VITE_IS_EMBEDDED_SIDECAR_BUILD);
}
import * as BUILD_CAPABILITIES from '#pplx/frontend/deployment-capabilities/build.js';
//# sourceMappingURL=useHandleClickPricing.js.map